import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { API_BASE_URL } from "../../config/api";

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("all");

  // ================= FETCH ORDERS =================
  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch(`${API_BASE_URL}/api/orders`);

        if (!res.ok) throw new Error("Server error");

        const data = await res.json();

        const formatted = (data || []).map((order) => {
          let product = null;

          try {
            product = order.order_details
              ? JSON.parse(order.order_details)
              : null;
          } catch {
            product = null;
          }

          return {
            id: order.id,
            name: order.name,
            surname: order.surname,
            email: order.email,
            mobile: order.mobile,
            address: order.address,
            status: order.status || "pending",
            createdAt: order.created_at,
            product,
          };
        });

        setOrders(formatted);
      } catch (err) {
        console.error(err);
        setError("Failed to load orders.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  // ================= STATUS UPDATE =================
  const handleStatusChange = async (id, status) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/orders/${id}/status`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status }),
        }
      );

      const data = await res.json();

      if (!res.ok || !data.success) {
        alert(data.error || "Failed");
        return;
      }

      setOrders((prev) =>
        prev.map((o) => (o.id === id ? { ...o, status } : o))
      );
    } catch (err) {
      alert("Server error");
    }
  };

  // ================= DATE FILTER =================
  const isInDateRange = (date) => {
    const d = new Date(date);
    const now = new Date();

    if (dateFilter === "today") {
      return d.toDateString() === now.toDateString();
    }

    if (dateFilter === "week") {
      const weekAgo = new Date();
      weekAgo.setDate(now.getDate() - 7);
      return d >= weekAgo;
    }

    if (dateFilter === "month") {
      const monthAgo = new Date();
      monthAgo.setMonth(now.getMonth() - 1);
      return d >= monthAgo;
    }

    return true;
  };

  // ================= FILTERED DATA =================
  const filteredOrders = orders.filter((o) => {
    const matchSearch =
      o.name?.toLowerCase().includes(search.toLowerCase()) ||
      o.surname?.toLowerCase().includes(search.toLowerCase()) ||
      o.mobile?.includes(search);

    const matchStatus =
      statusFilter === "All" ? true : o.status === statusFilter;

    const matchDate = isInDateRange(o.createdAt);

    return matchSearch && matchStatus && matchDate;
  });

  // ================= EXPORT EXCEL =================
  const exportExcel = () => {
    const exportData = filteredOrders.map((o) => ({
      ID: o.id,
      Emri: o.name,
      Mbiemri: o.surname,
      Telefoni: o.mobile,
      Statusi: o.status,
      Data: o.createdAt,
      Produkti: o.product?.name || "",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");

    XLSX.writeFile(wb, "orders.xlsx");
  };

  return (
    <div className="bg-gray-100 text-gray-800 min-h-screen">
      <main className="ml-64 overflow-auto p-6">

        {/* HEADER */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Manaxho Porositë</h1>

          <button
            onClick={exportExcel}
            className="bg-green-600 text-white px-4 py-2 rounded"
          >
            Export Excel
          </button>
        </div>

        {/* FILTERS */}
        <div className="flex gap-3 mb-4 flex-wrap">

          <input
            placeholder="Search..."
            className="border p-2 rounded"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            className="border p-2 rounded"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="shipped">Shipped</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            className="border p-2 rounded"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last Month</option>
          </select>

        </div>

        {/* LOADING */}
        {loading && <p>Loading...</p>}
        {error && <p className="text-red-600">{error}</p>}

        {/* TABLE */}
        {!loading && filteredOrders.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-x-auto w-full min-w-[1400px]">
            <table className="w-full text-sm border-collapse">

              <thead className="bg-blue-600 text-white">
                <tr>
                  <th className="p-3">ID</th>
                  <th className="p-3">Emri</th>
                  <th className="p-3">Mbiemri</th>
                  <th className="p-3">Telefoni</th>
                  <th className="p-3">Produkti</th>
                  <th className="p-3">Statusi</th>
                  <th className="p-3">Data</th>
                </tr>
              </thead>

              <tbody>
                {filteredOrders.map((o) => (
                  <tr key={o.id} className="border-b">

                    <td className="p-3">{o.id}</td>
                    <td className="p-3 font-semibold">{o.name}</td>
                    <td className="p-3 font-semibold">{o.surname}</td>
                    <td className="p-3">{o.mobile || "-"}</td>

                    <td className="p-3">
                      {o.product?.name || "No product"}
                    </td>

                    <td className="p-3">
                      <select
                        value={o.status}
                        onChange={(e) =>
                          handleStatusChange(o.id, e.target.value)
                        }
                        className="border px-3 py-1 rounded"
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="shipped">Shipped</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>

                    <td className="p-3">
                      {o.createdAt
                        ? new Date(o.createdAt).toLocaleString()
                        : "-"}
                    </td>

                  </tr>
                ))}
              </tbody>

            </table>
          </div>
        )}

      </main>
    </div>
  );
};

export default AdminOrders;